import React, { useState } from 'react';
import { validateFeatures, fixValidationIssues, importFeatures } from '../services/importService';
import { logger } from '../utils/logger';
import './ImportFeaturesWizard.css';

const ImportFeaturesWizard = ({ featureGroupId, onClose, onImportComplete }) => {
  const [step, setStep] = useState(1);
  const [fileContent, setFileContent] = useState(null);
  const [features, setFeatures] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState(new Set());
  const [selectedFeatureDetail, setSelectedFeatureDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      setError('Please upload a JSON file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = JSON.parse(e.target.result);
        setFileContent(content);
        setError(null);
      } catch (err) {
        setError('Invalid JSON file: ' + err.message);
        logger.error('Error parsing JSON:', err);
      }
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsText(file);
  };

  const handleValidate = () => {
    if (!fileContent) {
      setError('Please upload a file first');
      return;
    }

    try {
      const errors = validateFeatures(fileContent);
      setValidationErrors(errors);
      
      if (errors.length > 0) {
        // Fix issues automatically
        const fixed = fixValidationIssues(fileContent);
        setFeatures(fixed);
        // Select all features by default
        setSelectedFeatures(new Set(fixed.map(f => f.externalId)));
      } else {
        setFeatures(fileContent);
        // Select all features by default
        setSelectedFeatures(new Set(fileContent.map(f => f.externalId)));
      }
      
      setStep(2);
      setError(null);
    } catch (err) {
      setError('Validation failed: ' + err.message);
      logger.error('Validation error:', err);
    }
  };

  const handleToggleFeature = (externalId) => {
    const newSelected = new Set(selectedFeatures);
    if (newSelected.has(externalId)) {
      newSelected.delete(externalId);
    } else {
      newSelected.add(externalId);
    }
    setSelectedFeatures(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedFeatures(new Set(features.map(f => f.externalId)));
  };

  const handleDeselectAll = () => {
    setSelectedFeatures(new Set());
  };

  const handleFeatureClick = (feature) => {
    setSelectedFeatureDetail(feature);
  };

  const handleImport = async () => {
    if (selectedFeatures.size === 0) {
      setError('Please select at least one feature to import');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const result = await importFeatures(features, featureGroupId, selectedFeatures);
      setImportResult(result);
      setStep(3);
    } catch (err) {
      setError('Import failed: ' + err.message);
      logger.error('Import error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setFileContent(null);
    setFeatures([]);
    setValidationErrors([]);
    setSelectedFeatures(new Set());
    setSelectedFeatureDetail(null);
    setError(null);
    setImportResult(null);
  };

  return (
    <div className="import-wizard-overlay" onClick={onClose}>
      <div className="import-wizard" onClick={(e) => e.stopPropagation()}>
        <div className="import-wizard-header">
          <h2>Import Features</h2>
          <button className="import-wizard-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="import-wizard-steps">
          <div className={`import-wizard-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">Upload & Validate</div>
          </div>
          <div className={`import-wizard-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">Select Features</div>
          </div>
          <div className={`import-wizard-step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">Confirm</div>
          </div>
        </div>

        <div className="import-wizard-content">
          {step === 1 && (
            <div className="import-wizard-step-content">
              <h3>Step 1: Upload and Validate JSON</h3>
              <div className="file-upload-area">
                <input
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFileUpload}
                  className="file-input"
                  id="feature-import-file"
                />
                <label htmlFor="feature-import-file" className="file-upload-label">
                  <i className="fas fa-upload"></i>
                  <span>Choose JSON file to import</span>
                </label>
                {fileContent && (
                  <div className="file-info">
                    <i className="fas fa-check-circle"></i>
                    File loaded: {Array.isArray(fileContent) ? fileContent.length : 0} features
                  </div>
                )}
              </div>

              {validationErrors.length > 0 && (
                <div className="validation-errors">
                  <h4>Validation Issues (auto-fixed):</h4>
                  <ul>
                    {validationErrors.slice(0, 10).map((err, index) => (
                      <li key={index}>
                        <strong>{err.name}</strong> ({err.externalId}): {err.message}
                      </li>
                    ))}
                    {validationErrors.length > 10 && (
                      <li>... and {validationErrors.length - 10} more issues</li>
                    )}
                  </ul>
                </div>
              )}

              {error && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i> {error}
                </div>
              )}

              <div className="import-wizard-actions">
                <button className="cancel-button" onClick={onClose}>
                  Cancel
                </button>
                <button
                  className="next-button"
                  onClick={handleValidate}
                  disabled={!fileContent}
                >
                  Next: Select Features
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="import-wizard-step-content">
              <h3>Step 2: Select Features to Import</h3>
              <div className="feature-selection-header">
                <div className="selection-actions">
                  <button type="button" onClick={handleSelectAll}>
                    Select All
                  </button>
                  <button type="button" onClick={handleDeselectAll}>
                    Deselect All
                  </button>
                </div>
                <div className="selection-count">
                  {selectedFeatures.size} of {features.length} selected
                </div>
              </div>

              <div className="feature-selection-container">
                <div className="feature-list">
                  {features.map((feature) => (
                    <div
                      key={feature.externalId}
                      className={`feature-item ${selectedFeatures.has(feature.externalId) ? 'selected' : ''}`}
                      onClick={() => handleFeatureClick(feature)}
                    >
                      <label className="feature-checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedFeatures.has(feature.externalId)}
                          onChange={() => handleToggleFeature(feature.externalId)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="feature-name">{feature.name}</span>
                        {feature.parentExternalId && (
                          <span className="feature-parent-indicator">
                            (Child of: {features.find(f => f.externalId === feature.parentExternalId)?.name || feature.parentExternalId})
                          </span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>

                {selectedFeatureDetail && (
                  <div className="feature-detail-panel">
                    <div className="feature-detail-header">
                      <h4>{selectedFeatureDetail.name}</h4>
                      <button onClick={() => setSelectedFeatureDetail(null)}>
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                    <div className="feature-detail-content">
                      <div className="detail-section">
                        <strong>External ID:</strong> {selectedFeatureDetail.externalId}
                      </div>
                      <div className="detail-section">
                        <strong>Description:</strong>
                        <p>{selectedFeatureDetail.description || 'No description'}</p>
                      </div>
                      <div className="detail-section">
                        <strong>Type:</strong> {selectedFeatureDetail.type || 'feature'}
                      </div>
                      <div className="detail-section">
                        <strong>Position:</strong> {selectedFeatureDetail.position || 0}
                      </div>
                      {selectedFeatureDetail.parentExternalId && (
                        <div className="detail-section">
                          <strong>Parent:</strong> {features.find(f => f.externalId === selectedFeatureDetail.parentExternalId)?.name || selectedFeatureDetail.parentExternalId}
                        </div>
                      )}
                      {selectedFeatureDetail.metadata && (
                        <div className="detail-section">
                          <strong>Metadata:</strong>
                          <pre>{JSON.stringify(selectedFeatureDetail.metadata, null, 2)}</pre>
                        </div>
                      )}
                      <div className="detail-section">
                        <strong>Created:</strong> {selectedFeatureDetail.createdAt ? new Date(selectedFeatureDetail.createdAt).toLocaleString() : 'Unknown'}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i> {error}
                </div>
              )}

              <div className="import-wizard-actions">
                <button className="cancel-button" onClick={handleReset}>
                  Back
                </button>
                <button
                  className="next-button"
                  onClick={handleImport}
                  disabled={selectedFeatures.size === 0 || loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Importing...
                    </>
                  ) : (
                    'Next: Confirm'
                  )}
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="import-wizard-step-content">
              <h3>Step 3: Import Complete</h3>
              {importResult && (
                <div className="import-result">
                  <div className="import-success">
                    <i className="fas fa-check-circle"></i>
                    Successfully imported {importResult.imported.length} feature(s)
                  </div>
                  {importResult.errors.length > 0 && (
                    <div className="import-errors">
                      <h4>Errors ({importResult.errors.length}):</h4>
                      <ul>
                        {importResult.errors.map((err, index) => (
                          <li key={index}>
                            <strong>{err.name}</strong> ({err.externalId}): {err.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="import-wizard-actions">
                <button className="cancel-button" onClick={onClose}>
                  Close
                </button>
                {onImportComplete && (
                  <button className="next-button" onClick={onImportComplete}>
                    Done
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportFeaturesWizard;

